<script>
import lazyLoadComponent from '@router/lazyload-component'

import { genderFilter, orderStatusFilter, orderSourceFilter, prepaidOrderStatusFilter, orderPaymentMethodMap } from '@utils/filter-value-to-text'
import formatDate from '@utils/format-date'
import { PortalTarget, Portal } from 'portal-vue'

export default {
  name: 'PrepaidOrderDetail',
  components: {
    Portal,
    PortalTarget,
    BaseButton: () => lazyLoadComponent(import('@components/BaseButton')),
    BaseStateLabel: () => lazyLoadComponent(import('@components/BaseStateLabel')),
    BaseCollapse: () => lazyLoadComponent(import('@components/BaseCollapse')),
    OrderForm: () => lazyLoadComponent(import('@components/PrepaidOrderForm')),
    OrderChangeStatusDialog: () => lazyLoadComponent(import('@components/OrderChangeStatusDialog')),
    OrderMemberDialog: () => lazyLoadComponent(import('@components/OrderMemberDialog')),
    OrderDetail: () => lazyLoadComponent(import('@components/Order/OrderDetail')),
  },
  filters: {
    orderStatusFilter,
    prepaidOrderStatusFilter,
    genderFilter,
    orderSourceFilter,
    formatDate,
  },
  props: {
    defaultValue: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      mode: 'view', // view 檢視、edit 編輯
      shareDialogVisible: false,
      cancelDialogVisible: false,
      memberDialogVisible: false,
      tryingSendEmail: false,
      tryingSendSMS: false,
      shareLink: '',
      showCancelDeductOrder: false,
      drawerVisible: false,
      drawerOrderData: '',
      relatedChat: [],
      orderPaymentMethodMap,
    }
  },
  computed: {
    isPaid() {
      return this.defaultValue.status === 1
    },
    filterDeductOrders() {
      const deductedStatus = [1, 3, 4, 5, 6]
      return this.showCancelDeductOrder ? this.defaultValue.deductOrders : this.defaultValue.deductOrders.filter((order) => deductedStatus.includes(order.status))
    },
    paidAt() {
      if (!!this.defaultValue.paidAt === false) return '無'
      return formatDate(this.defaultValue.paidAt, 'dateTime')
    },
    paymentExpiredDate() {
      if (this.defaultValue.allowExpired === 1) return '無（可逾期付款）'
      return formatDate(this.defaultValue.paymentExpiredDate, 'dateTime')
    },
  },
  methods: {
    formatDate,
    updateMode(value) {
      this.mode = value
    },
    async tryToGetOrder(orderId) {
      this.drawerOrderData = await this.$store.dispatch('order/fetchSingle', orderId)
      this.drawerVisible = true
    },
    async handleStore() {
      const updatedValue = await this.$store.dispatch('prepaid/fetchOrderSingle', this.defaultValue.id)
      this.$emit('update:defaultValue', updatedValue)
      this.$emit('update')
      this.updateMode('view')
    },
    async tryToSendEmailOrSMS(router) {
      router === 'email' ? (this.tryingSendEmail = true) : (this.tryingSendSMS = true)
      try {
        await this.$store.dispatch('prepaid/sendLink', {
          orderId: this.defaultValue.id,
          router,
        })
        this.$alert(`已發送付款連結至 ${router}`, `${router} 發送成功！`, {
          confirmButtonText: '確認',
          center: true,
          callback: () => {
            router === 'email' ? (this.tryingSendEmail = false) : (this.tryingSendSMS = false)
          },
        })
      } catch (error) {
        router === 'email' ? (this.tryingSendEmail = false) : (this.tryingSendSMS = false)
      }
    },
    tryToSendSMS() {
      this.tryToSendEmailOrSMS('sms')
    },
    tryToSendEmail() {
      this.tryToSendEmailOrSMS('email')
    },
    handleCommand(command) {
      this[command]()
    },
    showCancelDialog() {
      this.cancelDialogVisible = true
    },
    showMemberDialog() {
      this.memberDialogVisible = true
    },
    async tryToFetchChat() {
      const orderInfo = this.defaultValue
      this.relatedChat = await this.$store.dispatch('member/fetchOrderRelatedChat', {
        phone: `${orderInfo.phoneCode}-${orderInfo.phoneNumber}`,
        page: 1,
        limit: 100,
        order_id: orderInfo.id,
      })
    },
    showChatHistory() {
      if (this.relatedChat.length > 1) {
        this.chatHistoryDialogVisible = true
      } else {
        this.$store.dispatch('member/updateMessageId', this.relatedChat[0])
        this.redirectToChatView(this.relatedChat[0].data.id)
      }
    },
  },
}
</script>

<template>
  <div>
    <h3>{{ `#${defaultValue.orderNo}` }}</h3>
    <p :class="$style.meta">
      <span>{{ defaultValue.created | formatDate('dateTime') }} 成立訂單</span>
      <BaseStateLabel state="info" text>訂單來源：{{ defaultValue.source | orderSourceFilter }}</BaseStateLabel>
    </p>
    <BaseStateLabel :state="defaultValue.status | orderStatusFilter('color')">{{ defaultValue.status | orderStatusFilter('text') }}</BaseStateLabel>

    <div :class="$style.header">
      <h5 :class="$style.title">{{ defaultValue.productName }}</h5>
      <div>{{ defaultValue.date | formatDate }} {{ defaultValue.time }}</div>
    </div>

    <!-- 編輯訂單 -->
    <OrderForm v-if="mode === 'edit'" :default-value="defaultValue" @store="handleStore" @cancel="updateMode('view')" />

    <template v-if="mode === 'view'">
      <BaseCollapse title="訂單變更紀錄">
        <el-timeline>
          <el-timeline-item v-for="log in defaultValue.history" :key="log.id" :timestamp="log.created | formatDate('dateTime')" type="primary" placement="top">
            {{ log.description }}
            <div v-if="log.note">{{ log.note }}</div>
          </el-timeline-item>
        </el-timeline>
      </BaseCollapse>
      <div :class="$style.params">
        <div :class="$style['params__item']">
          <div>訂購項目</div>
          <div>售價</div>
          <div>數量</div>
        </div>
        <div :class="$style['params__item']">
          <div>{{ defaultValue.prepaidTitle }}</div>
          <div class="Jost">{{ defaultValue.priceTotal }}</div>
          <div class="Jost">x1</div>
        </div>

        <template>
          <div :class="$style.total">
            <div :class="$style['total__item']">
              <div>付款金額</div>
              <div class="Jost">{{ defaultValue.priceTotal }}</div>
            </div>
          </div>
        </template>
        <div :class="$style.total">
          <div :class="$style['total__item']">
            <div>總額度</div>
            <div class="Jost">{{ defaultValue.times }} 次</div>
          </div>
          <div :class="$style['total__item']">
            <div>剩餘可用額度</div>
            <div class="Jost">{{ defaultValue.times - defaultValue.used }} 次</div>
          </div>
        </div>
      </div>
      <BaseCollapse title="客戶使用歷程">
        <el-switch v-model="showCancelDeductOrder" active-text="顯示待確認與已取消訂單"></el-switch>
        <el-timeline>
          <el-timeline-item
            v-for="order in filterDeductOrders"
            :key="order.id"
            :timestamp="`訂單成立時間：${formatDate(order.created, 'dateTime')}`"
            :type="order.status | prepaidOrderStatusFilter('color')"
          >
            <BaseStateLabel :state="order.status | prepaidOrderStatusFilter('color')" text>{{ order.status | prepaidOrderStatusFilter('text') }}</BaseStateLabel>

            <el-link :class="$style['history-title']" @click="tryToGetOrder(order.id)">{{ order.productTitle }}</el-link>
            <span :class="$style['history-meta']">・{{ order.startDate | formatDate('dateTime') }}</span>
            <div :class="$style['history-meta']" style="margin-top: 8px;">使用 {{ order.deductTimes }} 次抵扣</div>
          </el-timeline-item>
        </el-timeline>
      </BaseCollapse>
      <BaseCollapse title="付款資訊" :class="$style.payment">
        <ul style="padding: 0 25px;padding-bottom: 10px;">
          <li
            ><span class="title">付款方式</span><span class="content">{{ orderPaymentMethodMap[defaultValue.paymentMethod] || '不指定' }}</span>
          </li>
          <li
            ><span class="title">付款時間</span><span class="content">{{ paidAt }}</span></li
          >
          <li v-if="defaultValue.paymentMethod !== 'Vacc'"
            ><span class="title">付款期限</span><span class="content">{{ paymentExpiredDate }}</span></li
          >
          <template v-if="defaultValue.Vacc">
            <li style="border-top: 1px solid #dcdfe6;"
              ><span class="title">轉帳銀行</span><span class="content">{{ `${defaultValue.Vacc.Bank} ${defaultValue.Vacc.BankCode}` }}</span></li
            >
            <li
              ><span class="title">轉帳帳號</span><span class="content">{{ defaultValue.Vacc.VACCNo }}</span></li
            >
            <li
              ><span class="title">轉帳期限</span><span class="content">{{ defaultValue.paymentExpiredDate | formatDate('dateTime') }}</span></li
            >
          </template>
        </ul>
      </BaseCollapse>

      <h5 :class="$style.title">客戶資訊</h5>
      <ul :class="$style.info">
        <li>{{ defaultValue.fullName }} {{ defaultValue.gender | genderFilter }}</li>
        <li>{{ `${defaultValue.phoneCode} ${defaultValue.phoneNumber}` }}</li>
        <li>{{ defaultValue.email }}</li>
      </ul>

      <Portal to="drawer-prepaid-header">
        <template v-if="defaultValue.status !== 0">
          <el-button class="is-round" type="danger" @click="showCancelDialog">取消訂單</el-button>
          <el-button class="is-round" @click="updateMode('edit')">編輯訂單</el-button>
          <!-- <el-button @click="writeDialogVisible = true">撰寫通知</el-button> -->
          <el-dropdown ref="more" trigger="click" :hide-on-click="false" @command="handleCommand">
            <el-button class="is-round" style="margin-left: 10px;" @click="$refs.more && $refs.more.show()">更多 <BaseIcon name="caret-down"/></el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="showMemberDialog">查看客戶資訊</el-dropdown-item>
              <template v-can:api="'searchOrder'">
                <el-dropdown-item v-if="relatedChat.length > 0" command="showChatHistory"
                  >查看問答紀錄
                  <el-dialog title="請選擇欲查看的紀錄" :visible.sync="chatHistoryDialogVisible" top="0" width="40%" append-to-body>
                    <div :class="$style.chat">
                      <div v-for="(chat, index) in relatedChat" class="message" :key="index" @click.stop="onHistoryClick(chat)">
                        <div class="text">{{ chat.data.message || '客戶傳送了圖片。' }}</div>
                        <div class="name-date">
                          <div class="date">{{ chat.data.created_at | formatDate('dateTime') }}</div>
                        </div>
                      </div>
                    </div>
                  </el-dialog>
                </el-dropdown-item>
              </template>
              <template v-if="defaultValue.status === 2">
                <el-dropdown-item command="tryToSendSMS">發送付款連結至簡訊</el-dropdown-item>
                <el-dropdown-item v-loading="tryingSendEmail" command="tryToSendEmail">發送付款連結至 Email</el-dropdown-item>
              </template>
            </el-dropdown-menu>
          </el-dropdown>
        </template>
        <template v-else>
          <el-button @click="showMemberDialog">查看客戶資訊</el-button>
        </template>
      </Portal>
      <!-- <div :class="$style.buttons">
        <BaseButton icon="clipboard-list" @click="showMemberDialog">查看客戶資訊</BaseButton>
        <template v-if="defaultValue.status === 2">
          <BaseButton icon="paper-plane" @click="tryToSendEmailOrSMS('email')">發送付款連結至 Email</BaseButton>
          <BaseButton icon="sms" @click="tryToSendEmailOrSMS('sms')">發送付款連結至簡訊</BaseButton>
        </template>
        <template v-if="defaultValue.status !== 0">
          <BaseButton icon="edit" @click="updateMode('edit')">編輯訂單</BaseButton>
          <BaseButton icon="ban" @click="showCancelDialog">取消訂單</BaseButton>
        </template>
      </div> -->

      <OrderChangeStatusDialog :visible.sync="cancelDialogVisible" :order="{ ...defaultValue, status: 0 }" @cancelled="handleStore" />
      <OrderMemberDialog :visible.sync="memberDialogVisible" :member-id="defaultValue.memberId" />
    </template>
    <el-drawer :visible.sync="drawerVisible" append-to-body size="50%" :key="defaultValue.id" destroy-on-close>
      <!-- header -->

      <PortalTarget name="drawer-header" slot="title"></PortalTarget>
      <OrderDetail ref="orderDetail" :default-value.sync="drawerOrderData" />
      <el-button class="is-fullwidth is-drawer--bottom" plain @click="drawerVisible = false">回到客戶使用歷程</el-button>
    </el-drawer>
  </div>
</template>

<style lang="scss" module>
@import '@design';
:global {
  .el-timeline {
    margin-top: 15px;
  }
}

.meta {
  display: flex;
  justify-content: space-between;
}
.header {
  margin-bottom: 15px;
}
.title:not(:first-child) {
  margin-top: 30px;
}
.info {
  padding-inline-start: 0;
  > li {
    padding: 5px 0;
  }
}
.params {
  padding: 5px 15px;
  margin-top: 10px;
  margin-bottom: 10px;
  font-size: 14px;
  line-height: 40px;
  background: white;

  &__item {
    display: flex;
    justify-content: space-between;

    &:first-child {
      color: $color-text-placeholder;
    }
    > * {
      flex: 1;
      &:first-child {
        flex: 2;
      }
      &:not(:first-child) {
        text-align: right;
      }
    }
  }
}
.total {
  border-top: 1px solid $color-border-base;
  &__item {
    display: flex;
    justify-content: space-between;

    &--discount {
      color: $color-state-danger;
    }
    &:not(.total__item--discount) > *:first-child {
      color: $color-text-placeholder;
    }
  }
}
.history {
  &-title {
    margin-left: 10px;
  }
  &-meta {
    color: $color-text-placeholder;
  }
}

.buttons {
  margin-top: 30px;
  border-top: 1px solid $color-border-base;
  border-bottom: 1px solid $color-border-base;
}

.payment {
  margin-top: 10px;
  :global {
    li {
      line-height: 40px;
    }
    .title {
      margin-right: 20px;
      color: darkgray;
    }
  }
}
</style>
